/**
 * ConversationList Component
 * Displays list of AI conversations with search and actions
 */

import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  ChatBubbleOutline as ChatIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { AIConversation } from '../../services/AIConsoleService';
import { formatDistanceToNow } from 'date-fns';

const ListHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const SearchBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const StyledListItem = styled(ListItem)<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: selected ? theme.palette.action.selected : theme.palette.action.hover
  }
}));

const getContextTypeColor = (contextType: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' => {
  switch (contextType) {
    case 'project':
      return 'primary';
    case 'technical':
      return 'secondary';
    case 'estimation':
      return 'warning';
    default:
      return 'default';
  }
};

interface ConversationListProps {
  conversations: AIConversation[];
  selectedConversationId?: number;
  onSelect: (conversation: AIConversation) => void;
  onArchive: (conversationId: number) => void;
  onDelete: (conversationId: number) => void;
  onNewConversation: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  loading?: boolean;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedConversationId,
  onSelect,
  onArchive,
  onDelete,
  onNewConversation,
  searchQuery,
  onSearchChange,
  loading = false
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuConversationId, setMenuConversationId] = React.useState<number | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, conversationId: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuConversationId(conversationId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuConversationId(null);
  };

  const handleArchive = () => {
    if (menuConversationId) {
      onArchive(menuConversationId);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (menuConversationId) {
      onDelete(menuConversationId);
    }
    handleMenuClose();
  };

  return (
    <>
      <ListHeader>
        <Box display="flex" alignItems="center" gap={1}>
          <ChatIcon color="primary" />
          <Typography variant="h6">AI Console</Typography>
        </Box>
        <Tooltip title="New Conversation">
          <IconButton
            color="primary"
            onClick={onNewConversation}
            data-testid="new-conversation-button"
            size="small"
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </ListHeader>

      <SearchBox>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          data-testid="conversation-search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </SearchBox>

      <List sx={{ flex: 1, overflow: 'auto' }} data-testid="conversation-list">
        {conversations.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {searchQuery ? 'No conversations found' : 'No conversations yet'}
            </Typography>
          </Box>
        )}

        {conversations.map((conversation) => (
          <StyledListItem
            key={conversation.id}
            selected={conversation.id === selectedConversationId}
            data-testid={`conversation-${conversation.id}`}
            secondaryAction={
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => handleMenuOpen(e, conversation.id)}
                data-testid={`conversation-menu-${conversation.id}`}
              >
                <MoreIcon />
              </IconButton>
            }
          >
            <ListItemButton onClick={() => onSelect(conversation)}>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
                      {conversation.title}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Chip
                        label={conversation.context_type}
                        size="small"
                        color={getContextTypeColor(conversation.context_type)}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      {conversation.project && (
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {conversation.project.name}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {conversation.message_count || 0} messages • {' '}
                      {formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: true })}
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </StyledListItem>
        ))}
      </List>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        data-testid="conversation-menu"
      >
        <MenuItem onClick={handleArchive} data-testid="archive-conversation">
          <ArchiveIcon fontSize="small" sx={{ mr: 1 }} />
          Archive
        </MenuItem>
        <MenuItem onClick={handleDelete} data-testid="delete-conversation" sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export default ConversationList;
